import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { StudioPlanInfo, StudioWithPlan } from '../plans/types';

export function useStudioPlan() {
  const { getAuthToken } = useAuth();
  const [studio, setStudio] = useState<StudioWithPlan | null>(null);
  const [plan, setPlan] = useState<StudioPlanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const authToken = getAuthToken();
      if (!authToken) {
        setStudio(null);
        setPlan(null);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch studio plan');
      }

      const data = (await response.json()) as StudioWithPlan;
      setStudio(data);
      setPlan(data.plan ?? null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setStudio(null);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasCapability = useCallback(
    (capability: keyof StudioPlanInfo['capabilities']) => {
      if (loading || !plan) {
        return false;
      }
      return plan.capabilities[capability] === true;
    },
    [plan, loading]
  );

  return {
    studio,
    plan,
    loading,
    error,
    refresh,
    hasCapability,
    isFreePlan: plan?.accessState === 'free',
    canUseStripeBilling: !loading && !!plan && plan.capabilities.stripeBilling,
    canUseStripeConnect: !loading && !!plan && plan.capabilities.stripeConnect,
  };
}
